import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/AdminSidebar.scss';
import {
  FaThLarge, FaFolderOpen, FaTools, FaUsers, FaHome,
  FaPhoneAlt, FaImages, FaShareAlt, FaBriefcase,
  FaClipboardList, FaComments, FaEnvelope, FaSignOutAlt,
  FaChevronLeft, FaChevronRight, FaChevronDown, FaCogs, FaQuoteLeft,
  FaCreditCard, FaGraduationCap
} from 'react-icons/fa';

const AdminSidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isCMSOpen, setIsCMSOpen] = useState(false);
  const [isCareerOpen, setIsCareerOpen] = useState(false);
  const { logout, user } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path;

  const cmsItems = [
    { path: '/admin/services', label: 'Services', icon: <FaTools /> },
    { path: '/admin/team', label: 'Team', icon: <FaUsers /> },
    { path: '/admin/home', label: 'Home Page', icon: <FaHome /> },
    { path: '/admin/contact-info', label: 'Contact Info', icon: <FaPhoneAlt /> },
    { path: '/admin/gallery', label: 'Gallery', icon: <FaImages /> },
    { path: '/admin/testimonials', label: 'Testimonials', icon: <FaQuoteLeft /> },
    { path: '/admin/social', label: 'Social Links', icon: <FaShareAlt /> },
    { path: '/admin/collaborations', label: 'Collaborations', icon: <FaUsers /> },
  ];

  const mainItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: <FaThLarge /> },
    { path: '/admin/projects', label: 'Software Projects', icon: <FaFolderOpen /> },
    { path: '/admin/mba-projects', label: 'MBA Projects', icon: <FaFolderOpen /> },
    { path: '/admin/mba-universities', label: 'MBA Universities', icon: <FaGraduationCap /> },
    { path: '/admin/mba-enquiries', label: 'MBA Enquiries', icon: <FaClipboardList /> },
    { path: '/admin/software-enquiries', label: 'Software Enquiries', icon: <FaClipboardList /> },
    { path: '/admin/payment-enquiries', label: 'Payment CRM', icon: <FaCreditCard /> },
    { path: '/admin/completed-enquiries', label: 'Completed Archive', icon: <FaClipboardList /> },
  ];

  const careerItems = [
    { path: '/admin/applications', label: 'Job Applications', icon: <FaClipboardList /> },
    { path: '/admin/internship-applications', label: 'Internship Applications', icon: <FaUsers /> },
    { path: '/admin/custom-software-enquiries', label: 'Software Project Enquiries', icon: <FaClipboardList /> },
    { path: '/admin/custom-mba-enquiries', label: 'MBA Project Enquiries', icon: <FaClipboardList /> },
  ];

  const otherItems = [
    { path: '/admin/jobs', label: 'Jobs (Hiring)', icon: <FaBriefcase /> },
    { path: '/admin/contacts', label: 'Messages', icon: <FaComments /> },
    { path: '/admin/newsletter', label: 'Subscribers', icon: <FaEnvelope /> },
  ];

  const isAnyCMSActive = cmsItems.some(item => isActive(item.path));
  const isAnyCareerActive = careerItems.some(item => isActive(item.path));

  return (
    <aside className={`admin-sidebar ${isOpen ? 'open' : 'collapsed'}`}>
      <div className="sidebar-header">
        <div className="logo-section">
          {isOpen ? (
            <div className="admin-logo">
              <span className="logo-icon">P</span>
              <span className="logo-text">Pravara<span>Admin</span></span>
            </div>
          ) : (
            <span className="logo-icon mini">P</span>
          )}
        </div>
        <button
          className="toggle-btn"
          onClick={() => onToggle(!isOpen)}
          aria-label="Toggle sidebar"
        >
          {isOpen ? <FaChevronLeft /> : <FaChevronRight />}
        </button>
      </div>

      <nav className="sidebar-menu">
        {/* Main Items */}
        {mainItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
            title={item.label}
          >
            <span className="menu-icon">{item.icon}</span>
            {isOpen && <span className="menu-label">{item.label}</span>}
            {isActive(item.path) && isOpen && <div className="active-indicator" />}
          </Link>
        ))}

        {/* CMS Group */}
        <div className={`menu-group ${isCMSOpen || isAnyCMSActive ? 'group-open' : ''}`}>
          <div
            className={`menu-item group-header ${isAnyCMSActive ? 'active' : ''}`}
            onClick={() => setIsCMSOpen(!isCMSOpen)}
            title="CMS Management"
          >
            <span className="menu-icon"><FaCogs /></span>
            {isOpen && <span className="menu-label">CMS Management</span>}
            {isOpen && (
              <span className={`chevron ${isCMSOpen ? 'rotated' : ''}`}>
                <FaChevronDown />
              </span>
            )}
            {isAnyCMSActive && isOpen && !isCMSOpen && <div className="active-indicator" />}
          </div>

          {(isCMSOpen || !isOpen) && (
            <div className="sub-menu">
              {cmsItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`menu-item sub-item ${isActive(item.path) ? 'active' : ''}`}
                  title={item.label}
                >
                  <span className="menu-icon">{item.icon}</span>
                  {isOpen && <span className="menu-label">{item.label}</span>}
                  {isActive(item.path) && isOpen && <div className="active-indicator sub" />}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Career Management Group */}
        <div className={`menu-group ${isCareerOpen || isAnyCareerActive ? 'group-open' : ''}`}>
          <div
            className={`menu-item group-header ${isAnyCareerActive ? 'active' : ''}`}
            onClick={() => setIsCareerOpen(!isCareerOpen)}
            title="Career Management"
          >
            <span className="menu-icon"><FaBriefcase /></span>
            {isOpen && <span className="menu-label">Career Management</span>}
            {isOpen && (
              <span className={`chevron ${isCareerOpen ? 'rotated' : ''}`}>
                <FaChevronDown />
              </span>
            )}
            {isAnyCareerActive && isOpen && !isCareerOpen && <div className="active-indicator" />}
          </div>

          {(isCareerOpen || !isOpen) && (
            <div className="sub-menu">
              {careerItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`menu-item sub-item ${isActive(item.path) ? 'active' : ''}`}
                  title={item.label}
                >
                  <span className="menu-icon">{item.icon}</span>
                  {isOpen && <span className="menu-label">{item.label}</span>}
                  {isActive(item.path) && isOpen && <div className="active-indicator sub" />}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Other Items */}
        {otherItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`menu-item ${isActive(item.path) ? 'active' : ''}`}
            title={item.label}
          >
            <span className="menu-icon">{item.icon}</span>
            {isOpen && <span className="menu-label">{item.label}</span>}
            {isActive(item.path) && isOpen && <div className="active-indicator" />}
          </Link>
        ))}
      </nav>

      <div className="sidebar-footer">
        {isOpen && user && (
          <div className="user-profile">
            <div className="user-avatar">
              {user.username?.charAt(0).toUpperCase() || 'A'}
            </div>
            <div className="user-details">
              <strong>{user.username || 'Admin'}</strong>
              <span>Administrator</span>
            </div>
          </div>
        )}
        <button onClick={handleLogout} className="logout-btn" title="Logout">
          <span className="menu-icon"><FaSignOutAlt /></span>
          {isOpen && <span className="menu-label">Logout</span>}
        </button>
      </div>
    </aside>
  );
};

export default AdminSidebar;
